import { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
// @mui
// hooks
import { useResponsiveBreakpoints } from 'src/hooks/useResponsiveBreakpoints';
// components
import { useSettingsContext } from '../../components/settings';
//
import { useUserContext } from 'src/auth/useUserContext';
import { useOrganizationContext } from 'src/sections/@dashboard/organization/hooks/useOrganizationContext';
import { PATH_DASHBOARD } from '../../routes/paths';
import ContentBreakpoints from './ContentBreakpoints';
import Main from './Main';
import { SidebarProvider, useSidebarContext } from './SidebarContext';
import Header from './header';
import NavHorizontal from './nav/NavHorizontal';
import NavVertical from './nav/NavVertical';

// ----------------------------------------------------------------------

/** Staff without a clinic can only reach their invitations, so no menu is shown. */
function useShowNav(): boolean {
  const { pathname } = useLocation();
  const {
    isProfessionalRole,
    isOrganizationAdminRole,
    isOrganizationOwnerRole,
    isAdminRole,
  } = useUserContext();
  const { isOrganizationNotVerified, organizations } = useOrganizationContext();
  const isStaffWithoutOrganizations =
    (isProfessionalRole ||
      isOrganizationAdminRole ||
      isOrganizationOwnerRole) &&
    !isAdminRole &&
    organizations.length === 0;
  return (
    !pathname.startsWith(PATH_DASHBOARD.general.updateAccount) &&
    !isOrganizationNotVerified &&
    !isStaffWithoutOrganizations
  );
}

function DashboardLayoutInner() {
  const { themeLayout } = useSettingsContext();
  const { isUserRole } = useUserContext();
  const isUserRoleDefined = isUserRole !== undefined;
  const { isLg } = useResponsiveBreakpoints();
  const { sidebarOpen, isProSidebar } = useSidebarContext();

  const [open, setOpen] = useState(false);

  const isNavHorizontal = themeLayout === 'horizontal';

  const showNav = useShowNav();

  const isProDesktop = isProSidebar && !isLg;

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // Pro/admin user on desktop: vertical sidebar layout
  if (isProDesktop) {
    return (
      <>
        {isUserRoleDefined && <Header onOpenNav={handleOpen} isProSidebar />}
        {showNav && <NavVertical openNav={open} onCloseNav={handleClose} />}
        <Main>
          <ContentBreakpoints>
            <Outlet />
          </ContentBreakpoints>
        </Main>
      </>
    );
  }

  // Mobile (all users): temporary drawer
  if (isNavHorizontal && isLg) {
    return (
      <>
        {isUserRoleDefined && <Header onOpenNav={handleOpen} />}
        {showNav ? (
          <NavVertical openNav={open} onCloseNav={handleClose} />
        ) : null}
        <Main>
          <Outlet />
        </Main>
      </>
    );
  }

  // Regular user on desktop: horizontal nav
  return (
    <>
      {isUserRoleDefined && <Header onOpenNav={handleOpen} />}
      {showNav ? <NavHorizontal /> : null}
      <Main>
        <Outlet />
      </Main>
    </>
  );
}

export default function DashboardLayout() {
  const { isUserRole } = useUserContext();

  // Use localStorage hint for returning pro users to avoid horizontal nav flash
  const hasProSidebarHint =
    isUserRole === undefined &&
    typeof window !== 'undefined' &&
    localStorage.getItem('pro-sidebar-open') !== null;

  const showNav = useShowNav();

  const isProSidebar = (isUserRole === false || hasProSidebarHint) && showNav;

  return (
    <SidebarProvider isProSidebar={isProSidebar}>
      <DashboardLayoutInner />
    </SidebarProvider>
  );
}
